import React from "react";
import { Conditional } from "web/react/components/conditional";
import { useProductCardContext } from "web/react/components/product-card/react";
import { ProductCardLink } from "web/react/components/product-card/react/product-card-link";
import { Retailer, SponsoredByRetailer } from "web/react/emo/retailer/retailer";
import { BaseText } from "web/react/emo/shared/components/base-text";
import { Text } from "web/react/emo/text";
import { VStack } from "web/react/emo/v-stack";
import { View } from "web/react/emo/view";
import analytics from "web/script/analytics/analytics";
import { gettext } from "web/script/modules/django-i18n";
import * as styles from "./product-details.css";

interface ProductCardDetailsProps {
    showRetailer?: boolean;
    showPromo?: boolean;
    feedType?: string;
    analyticsEventLabel?: string;
    reason?: string;
    shouldRenderLink?: boolean;
    showSponsoredMessaging?: boolean;
}

export function ProductDetails({
    showRetailer = true,
    showPromo = true,
    feedType,
    analyticsEventLabel,
    reason,
    shouldRenderLink = true,
    showSponsoredMessaging = false,
}: ProductCardDetailsProps): JSX.Element {
    const {
        designer_name: brandName,
        short_description: shortDescription,
        retailer_name: retailerName,
        sale_discount: saleDiscount,
        in_stock: inStock,
        show_retailer_as_affiliate: showRetailerAsAffiliate,
    } = useProductCardContext();

    const promo = getPromoLabel();

    // TODO CW-318 move this logic of getting the promo on the BE same as mobile_api
    function getPromoLabel(): string | undefined {
        if (!inStock) return gettext("in_stock_product.compare_prices.out_of_stock_option");
        if (saleDiscount) return gettext("product_card.promo_label.sale");
    }

    function handleProductLinkClick(): void {
        analytics.event("product_card", "product", analyticsEventLabel, false, {
            designer_name: brandName,
            retailer_name: retailerName,
            in_stock: inStock,
        });
    }

    function handleShortDescriptionClick(): void {
        analytics.event("product_card", "short description", analyticsEventLabel, false, {
            designer_name: brandName,
            retailer_name: retailerName,
            in_stock: inStock,
        });
    }

    function handleRetailerClick(): void {
        analytics.event("product_card", "retailer", analyticsEventLabel);
    }

    return (
        <View>
            <VStack spacing="xxs">
                <VStack spacing="xxxs">
                    <Conditional check={Boolean(shouldRenderLink)}>
                        <ProductCardLink
                            reason={reason || "text"}
                            onClick={handleProductLinkClick}
                            feedType={feedType}
                        >
                            <VStack spacing="none">
                                <Text
                                    as="span"
                                    textStyle="callout-small"
                                    color={"secondary"}
                                    lineLimit={1}
                                >
                                    {brandName}
                                </Text>
                                <Conditional check={!!shortDescription}>
                                    <div
                                        onClick={handleShortDescriptionClick}
                                        className={styles.cursorPointer}
                                    >
                                        <Text
                                            as="span"
                                            textStyle="body-3"
                                            color={"secondary"}
                                            lineLimit={2}
                                            aria-description={gettext(
                                                "product_card.product_description.label"
                                            )}
                                        >
                                            {shortDescription}
                                        </Text>
                                    </div>
                                </Conditional>
                            </VStack>
                        </ProductCardLink>
                    </Conditional>
                    <Conditional check={Boolean(!shouldRenderLink)}>
                        <VStack spacing="none">
                            <Text
                                as="span"
                                textStyle="callout-small"
                                color={"secondary"}
                                lineLimit={1}
                            >
                                {brandName}
                            </Text>
                            <Conditional check={!!shortDescription}>
                                <div
                                    onClick={handleShortDescriptionClick}
                                    className={styles.cursorPointer}
                                >
                                    <Text
                                        as="span"
                                        textStyle="body-3"
                                        color={"secondary"}
                                        lineLimit={2}
                                        aria-description={gettext(
                                            "product_card.product_description.label"
                                        )}
                                    >
                                        {shortDescription}
                                    </Text>
                                </div>
                            </Conditional>
                        </VStack>
                    </Conditional>

                    <Conditional check={Boolean(showRetailer && retailerName)}>
                        <div onClick={handleRetailerClick} data-testid="retailer">
                            <Text as="span" textStyle="callout-small" color={"secondary"}>
                                {showSponsoredMessaging ? (
                                    <SponsoredByRetailer
                                        retailerName={retailerName}
                                        showLinkIcon={showRetailerAsAffiliate}
                                    />
                                ) : (
                                    <Retailer
                                        retailerName={retailerName as string}
                                        fromSpanStyle={"capitalize"}
                                        showLinkIcon={showRetailerAsAffiliate}
                                    />
                                )}
                            </Text>
                        </div>
                    </Conditional>
                </VStack>
                <Conditional check={Boolean(showPromo && promo)}>
                    <BaseText as="span" textStyle={"caption-2"} color={"secondaryBrand"} upperCase>
                        {promo}
                    </BaseText>
                </Conditional>
            </VStack>
        </View>
    );
}
